import { useState, useEffect } from 'react';
import api from '../../api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Bot, Send, Loader2, Copy, Sparkles, MessageSquare, AlertTriangle, Megaphone, RefreshCw } from 'lucide-react';

export default function AdminAIAssistant() {
  const [prompt, setPrompt] = useState('');
  const [context, setContext] = useState('');
  const [messageType, setMessageType] = useState('general');
  const [response, setResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [activeTab, setActiveTab] = useState('compose');

  useEffect(() => { fetchSuggestions(); }, []);

  const fetchSuggestions = async () => {
    try {
      const res = await api.post('/admin/ai-assistant/suggestions');
      setSuggestions(res.data);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) { toast.error('Please enter a prompt'); return; }
    
    setIsLoading(true);
    setResponse('');
    
    try {
      const res = await api.post('/admin/ai-assistant', {
        prompt: prompt.trim(),
        context: context.trim() || null,
        message_type: messageType
      });
      setResponse(res.data.response);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate response');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleUseSuggestion = (text) => {
    setResponse(text);
    setActiveTab('compose');
  };

  const quickPrompts = [
    { label: 'Approve Deposit', prompt: 'Write a message to inform a customer their deposit has been approved' },
    { label: 'Reject Deposit', prompt: 'Write a polite message explaining why a deposit was rejected' },
    { label: 'Resolve Dispute', prompt: 'Write a message to close a dispute and offer a resolution' },
    { label: 'New Product', prompt: 'Write an announcement for a new product launch' },
    { label: 'Maintenance', prompt: 'Write a notice about scheduled system maintenance' },
    { label: 'Welcome', prompt: 'Write a welcome message for new users' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit'] flex items-center gap-2">
            <Bot className="w-7 h-7 text-[#FFD700]" />
            AI Writing Assistant
          </h1>
          <p className="text-zinc-500">Powered by GPT-5.2 to help you write professional messages</p>
        </div>
        <Badge className="bg-[#FFD700]/20 text-[#FFD700] font-bold">
          <Sparkles className="w-3 h-3 mr-1" />AI Powered
        </Badge>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#0A0A0A] border-2 border-[#FFD700]/30">
          <TabsTrigger value="compose">Compose</TabsTrigger>
          <TabsTrigger value="templates">Quick Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="compose" className="mt-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Input Section */}
            <Card className="bg-[#0A0A0A] border-2 border-[#FFD700]/30">
              <CardHeader>
                <CardTitle className="text-white text-lg">What do you want to write?</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Message Type */}
                  <div>
                    <Label className="text-[#FFD700] font-bold uppercase text-xs">Message Type</Label>
                    <Select value={messageType} onValueChange={setMessageType}>
                      <SelectTrigger className="bg-[#121212] border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="general">
                          <span className="flex items-center gap-2"><MessageSquare className="w-4 h-4" />General Message</span>
                        </SelectItem>
                        <SelectItem value="dispute">
                          <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" />Dispute Response</span>
                        </SelectItem>
                        <SelectItem value="announcement">
                          <span className="flex items-center gap-2"><Megaphone className="w-4 h-4" />Announcement</span>
                        </SelectItem>
                        <SelectItem value="chat">
                          <span className="flex items-center gap-2"><MessageSquare className="w-4 h-4" />Chat Reply</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Quick Prompts */}
                  <div>
                    <Label className="text-zinc-400 text-xs mb-2 block">Quick Prompts</Label>
                    <div className="flex flex-wrap gap-2">
                      {quickPrompts.map((qp, idx) => (
                        <Button
                          key={idx}
                          type="button"
                          variant="outline"
                          size="sm"
                          className="border-white/10 text-white hover:border-[#FFD700]/50 hover:text-[#FFD700] text-xs"
                          onClick={() => setPrompt(qp.prompt)}
                        >
                          {qp.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Context */}
                  <div>
                    <Label className="text-[#FFD700] font-bold uppercase text-xs">Context (Optional)</Label>
                    <Textarea
                      value={context}
                      onChange={(e) => setContext(e.target.value)}
                      placeholder="Add context like customer name, order details, issue description..."
                      className="bg-[#121212] border-white/10 text-white h-20"
                    />
                  </div>

                  {/* Prompt */}
                  <div>
                    <Label className="text-[#FFD700] font-bold uppercase text-xs">What do you want to say?</Label>
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="e.g., Write a professional response apologizing for the delay and offering a solution..."
                      className="bg-[#121212] border-white/10 text-white h-24"
                      required
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isLoading} 
                    className="w-full bg-[#FFD700] text-black font-bold hover:bg-[#E5C100]"
                  >
                    {isLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" />Generating...</>
                    ) : (
                      <><Sparkles className="w-4 h-4 mr-2" />Generate Message</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Output Section */}
            <Card className="bg-[#0A0A0A] border-2 border-white/10">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white text-lg">Generated Message</CardTitle>
                {response && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(response)}
                      className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10"
                    >
                      <Copy className="w-4 h-4 mr-1" />Copy
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSubmit}
                      disabled={isLoading}
                      className="border-white/10 hover:border-white/30"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#FFD700] mb-4" />
                    <p className="text-zinc-500">AI is writing your message...</p>
                  </div>
                ) : response ? (
                  <div className="bg-[#121212] rounded-lg p-4 border border-white/10">
                    <p className="text-white whitespace-pre-wrap">{response}</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Bot className="w-16 h-16 text-zinc-600 mb-4" />
                    <p className="text-zinc-500">Your AI-generated message will appear here</p>
                    <p className="text-zinc-600 text-sm mt-2">Fill in the form and click "Generate Message"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Dispute Responses */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  Dispute Responses
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {suggestions?.dispute_responses?.map((text, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => handleUseSuggestion(text)}
                    className="p-3 bg-[#121212] rounded-lg border border-white/10 cursor-pointer hover:border-[#FFD700]/30 transition-colors"
                  >
                    <p className="text-sm text-zinc-300">{text}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Deposit Approved */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  Deposit Approved
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {suggestions?.deposit_approved?.map((text, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => handleUseSuggestion(text)}
                    className="p-3 bg-[#121212] rounded-lg border border-white/10 cursor-pointer hover:border-[#FFD700]/30 transition-colors"
                  >
                    <p className="text-sm text-zinc-300">{text}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Deposit Rejected */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <span className="text-red-500">✗</span>
                  Deposit Rejected
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {suggestions?.deposit_rejected?.map((text, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => handleUseSuggestion(text)}
                    className="p-3 bg-[#121212] rounded-lg border border-white/10 cursor-pointer hover:border-[#FFD700]/30 transition-colors"
                  >
                    <p className="text-sm text-zinc-300">{text}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Order Issues */}
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-500" />
                  Order Issues
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {suggestions?.order_issues?.map((text, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => handleUseSuggestion(text)}
                    className="p-3 bg-[#121212] rounded-lg border border-white/10 cursor-pointer hover:border-[#FFD700]/30 transition-colors"
                  >
                    <p className="text-sm text-zinc-300">{text}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Announcements */}
            <Card className="bg-[#0A0A0A] border-white/10 md:col-span-2">
              <CardHeader>
                <CardTitle className="text-white text-sm flex items-center gap-2">
                  <Megaphone className="w-4 h-4 text-[#FFD700]" />
                  Announcements
                </CardTitle>
              </CardHeader>
              <CardContent className="grid md:grid-cols-3 gap-2">
                {suggestions?.announcements?.map((text, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => handleUseSuggestion(text)}
                    className="p-3 bg-[#121212] rounded-lg border border-white/10 cursor-pointer hover:border-[#FFD700]/30 transition-colors"
                  >
                    <p className="text-sm text-zinc-300">{text}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <p className="text-xs text-zinc-500 text-center mt-4">
            Click any template to use it, then customize in the Compose tab
          </p>
        </TabsContent>
      </Tabs>
    </div>
  );
}
